import { Loader } from '@/components/prompt-kit/loader'
import { Message, MessageAction, MessageActions, MessageAvatar, MessageContent } from '@/components/prompt-kit/message'
import { TextShimmer } from '@/components/prompt-kit/text-shimmer'
import { Tool, ToolPart } from '@/components/prompt-kit/tool'
import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { IconSurveyScale5Outline } from '@icons/IconSurveyScale5Outline'
import { ArrowDown, Copy, Layers, RotateCcw, Square, ThumbsDown, ThumbsUp, Wrench } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { AiComponentRenderer } from './ai-component-library/AiComponentRenderer'
import { ChatCatalogComponents } from './ChatCatalogComponents'
import { AgentStep, ChatContextState, ChatMessage } from './types'
import { BOT_CONFIGS, useAiChat } from './useAiChat'
// import { AiRadarChart } from './ai-component-library/ai-radar-chart/AiRadarChart'
// import mockAiRadarChart from './ai-component-library/ai-radar-chart/mock-ai-radar-chart.json'
// import { AiRadarChartSchema } from './ai-component-library/ai-radar-chart/types-ai-radar-chart'

// ============================================================================
// Empty State
// ============================================================================

export function EmptyState() {
  const { botType } = useAiChat()
  const botConfig = BOT_CONFIGS.find((config) => config.value === botType)
  return (
    <div className="flex h-full flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-primary/10 p-4">
        <IconSurveyScale5Outline className="size-8 text-primary" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">{`Start a conversation ${botConfig?.label ? `with the '${botConfig.label}' agent` : ''}`}</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">{botConfig?.description}</p>
    </div>
  )
}

// ============================================================================
// Chat Message Item
// ============================================================================

interface ChatMessageItemProps {
  message: ChatMessage
  isLast: boolean
  /** True while waiting for the worker to confirm a user-initiated stop */
  isStopping?: boolean
  /** True once the worker has joined the chat channel for the in-flight turn */
  agentConnected?: boolean
  /** True after TURN_LONG_WAIT_MS while the pending bubble is still empty */
  showLongWaitHint?: boolean
  /** True when cancel/stop is available for the in-flight turn */
  canAbortTurn?: boolean
  /** True while the worker summarises earlier context before the turn runs */
  isContextCompacting?: boolean
  /** Brief banner after a successful compaction on the current turn */
  showContextCompactedNotice?: boolean
  /** Brief banner when compaction was skipped */
  showContextSkippedNotice?: boolean
  /** Latest context telemetry for compaction copy */
  contextState?: ChatContextState | null
  onAbortTurn?: () => void
}

export function ChatMessageItem({
  message,
  isLast,
  isStopping = false,
  agentConnected = false,
  showLongWaitHint = false,
  canAbortTurn = false,
  isContextCompacting = false,
  showContextCompactedNotice = false,
  showContextSkippedNotice = false,
  contextState = null,
  onAbortTurn,
}: ChatMessageItemProps) {
  const isAssistant = message.role === 'assistant'
  const hasCatalogRequests = Boolean(message.catalogRequests && message.catalogRequests.length > 0)
  const hasAiComponents = Boolean(message.aiComponents && message.aiComponents.length > 0)

  // Copy message content to clipboard
  const handleCopy = useCallback((content: string) => {
    navigator.clipboard.writeText(content)
    toast.success('Copied to clipboard')
  }, [])

  return (
    <Message
      className={cn(
        // 'mx-auto flex w-full flex-col gap-2',
        'mx-auto flex w-full max-w-4xl flex-col gap-2',
        // isAssistant && hasCatalogRequests ? 'max-w-[calc(100vw-3rem)] items-start' : 'max-w-3xl',
        // isAssistant && hasCatalogRequests ? 'max-w-[calc(100vw-3rem)] items-start' : 'max-w-4xl',
        !isAssistant && 'items-end',
        isAssistant && !hasCatalogRequests && !hasAiComponents && 'items-start'
      )}
    >
      {isAssistant ? (
        <AssistantMessage
          message={message}
          isLast={isLast}
          isStopping={isStopping}
          agentConnected={agentConnected}
          showLongWaitHint={showLongWaitHint}
          canAbortTurn={canAbortTurn}
          isContextCompacting={isContextCompacting}
          showContextCompactedNotice={showContextCompactedNotice}
          showContextSkippedNotice={showContextSkippedNotice}
          contextState={contextState}
          onAbortTurn={onAbortTurn}
          onCopy={handleCopy}
        />
      ) : (
        <UserMessage message={message} onCopy={handleCopy} />
      )}
    </Message>
  )
}

// ============================================================================
// User Message
// ============================================================================

interface UserMessageProps {
  message: ChatMessage
  onCopy: (content: string) => void
}

function UserMessage({ message, onCopy }: UserMessageProps) {
  return (
    <div className="group flex w-full flex-col items-end gap-1">
      {/* <MessageContent className="max-w-[85%] rounded-2xl bg-primary px-4 py-2.5 text-primary-foreground sm:max-w-[75%]"> */}
      <MessageContent className="w-fit max-w-[85%] rounded-2xl bg-muted px-4 py-2.5 text-foreground shadow sm:max-w-[75%]">
        {message.content}
      </MessageContent>
      <MessageActions className="flex gap-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
        <MessageAction tooltip="Copy" delayDuration={100}>
          <Button variant="ghost" size="icon" className="size-7 rounded-full" onClick={() => onCopy(message.content)}>
            <Copy className="size-3.5" />
          </Button>
        </MessageAction>
      </MessageActions>
    </div>
  )
}

// ============================================================================
// Assistant Message
// ============================================================================

interface AssistantMessageProps {
  message: ChatMessage
  isLast: boolean
  isStopping?: boolean
  agentConnected?: boolean
  showLongWaitHint?: boolean
  canAbortTurn?: boolean
  isContextCompacting?: boolean
  showContextCompactedNotice?: boolean
  showContextSkippedNotice?: boolean
  contextState?: ChatContextState | null
  onAbortTurn?: () => void
  onCopy: (content: string) => void
}

function AssistantMessage({
  message,
  isLast,
  isStopping = false,
  agentConnected = false,
  showLongWaitHint = false,
  canAbortTurn = false,
  isContextCompacting = false,
  showContextCompactedNotice = false,
  showContextSkippedNotice = false,
  contextState = null,
  onAbortTurn,
  onCopy,
}: AssistantMessageProps) {
  const hasThinking = Boolean(message.thinking && message.thinking.length > 0)
  const hasContent = Boolean(message.content && message.content.length > 0)
  const hasSteps = Boolean(message.steps && message.steps.length > 0)
  const hasCatalogRequests = Boolean(message.catalogRequests && message.catalogRequests.length > 0)
  const hasAiComponents = Boolean(message.aiComponents && message.aiComponents.length > 0)

  // Separate tool calls from reasoning steps
  const toolSteps = message.steps?.filter((s) => s.toolName) ?? []
  const reasoningSteps = message.steps?.filter((s) => !s.toolName) ?? []
  const showStoppingState = Boolean(isStopping && message.isStreaming)
  const showStoppedNotice = Boolean(message.cancelled && !message.isStreaming)
  const showCompactingNotice = Boolean(isContextCompacting && message.isStreaming)
  const showCompactedNotice = Boolean(showContextCompactedNotice)
  const showSkippedNotice = Boolean(showContextSkippedNotice)
  const showPendingReply = Boolean(
    message.isStreaming &&
      !showStoppingState &&
      !showCompactingNotice &&
      !hasContent &&
      !hasSteps &&
      !hasThinking &&
      !hasAiComponents
  )
  const pendingReplyLabel = agentConnected ? 'Thinking...' : 'Processing...'

  return (
    <div className="group flex w-full flex-col gap-3">
      {showPendingReply && (
        <AssistantPendingReply
          label={pendingReplyLabel}
          showLongWait={showLongWaitHint}
          workerHasStarted={agentConnected}
          onAbort={canAbortTurn ? onAbortTurn : undefined}
          isAborting={isStopping}
        />
      )}

      {showStoppingState && <StoppingNotice />}

      {showCompactingNotice && <CompactingNotice />}

      {showCompactedNotice && <CompactedNotice contextState={contextState} />}

      {showSkippedNotice && <CompactionSkippedNotice />}

      <div>
        {/* Reasoning steps - auto-collapses when content starts streaming */}
        {(hasThinking || reasoningSteps.length > 0) && (
          <ReasoningSection
            isStreaming={Boolean(message.isStreaming && !hasContent && !showStoppingState)}
            hasContent={hasContent}
            thinking={message.thinking}
            reasoningSteps={reasoningSteps}
          />
        )}

        {/* Tool calls using the Tool component */}
        {toolSteps.length > 0 && (
          <div>
            {toolSteps.map((step, index) => (
              <AgentToolCall
                key={`${message.id}-tool-${index}-${step.stepNumber}-${step.toolName ?? 'tool'}`}
                step={step}
              />
            ))}
          </div>
        )}
      </div>

      {hasCatalogRequests && (
        <div className="w-full">
          <ChatCatalogComponents
            components={message.catalogComponents ?? []}
            requests={message.catalogRequests ?? []}
          />
        </div>
      )}

      {/* Main response content */}
      {hasContent && (
        <div className="flex items-start gap-5">
          <MessageAvatar src="" alt="AI" fallback="AI" />
          <MessageContent className="prose w-full max-w-none rounded-lg bg-transparent p-0 text-foreground" markdown>
            {message.content}
          </MessageContent>
        </div>
      )}

      {hasAiComponents && (
        <div className="w-full">
          <AiComponentRenderer components={message.aiComponents ?? []} />
        </div>
      )}

      {/* <AiRadarChart schema={mockAiRadarChart.schema as AiRadarChartSchema} /> */}

      {/* Streaming indicator when there's content being added */}
      {message.isStreaming && !showStoppingState && (hasContent || hasCatalogRequests || hasAiComponents) && (
        <div className="flex items-center gap-2">
          <Loader variant="typing" size="sm" />
        </div>
      )}

      {showStoppedNotice && <StoppedNotice />}

      {/* Actions - shown when message is complete */}
      {!message.isStreaming && hasContent && (
        <MessageActions
          className={cn(
            '-ml-2 flex gap-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100',
            isLast && 'opacity-100'
          )}
        >
          <MessageAction tooltip="Copy" delayDuration={100}>
            <Button variant="ghost" size="icon" className="size-8 rounded-full" onClick={() => onCopy(message.content)}>
              <Copy className="size-4" />
            </Button>
          </MessageAction>
          <MessageAction tooltip="Good response" delayDuration={100}>
            <Button variant="ghost" size="icon" className="size-8 rounded-full">
              <ThumbsUp className="size-4" />
            </Button>
          </MessageAction>
          <MessageAction tooltip="Bad response" delayDuration={100}>
            <Button variant="ghost" size="icon" className="size-8 rounded-full">
              <ThumbsDown className="size-4" />
            </Button>
          </MessageAction>
          <MessageAction tooltip="Regenerate" delayDuration={100}>
            <Button variant="ghost" size="icon" className="size-8 rounded-full">
              <RotateCcw className="size-4" />
            </Button>
          </MessageAction>
        </MessageActions>
      )}
    </div>
  )
}

// ============================================================================
// Pending reply (pre-first-token)
// ============================================================================

interface AssistantPendingReplyProps {
  label: string
  showLongWait: boolean
  workerHasStarted: boolean
  onAbort?: () => void
  isAborting?: boolean
}

function AssistantPendingReply({
  label,
  showLongWait,
  workerHasStarted,
  onAbort,
  isAborting = false,
}: AssistantPendingReplyProps) {
  const abortLabel = workerHasStarted ? 'Stop agent' : 'Cancel request?'

  return (
    <div role="status" aria-live="polite">
      <div className="flex items-start gap-5">
        <MessageAvatar src="" alt="AI" fallback="AI" />
        <div className="flex flex-col gap-1.5 pt-0.5">
          <TextShimmer>{label}</TextShimmer>
          {showLongWait && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <span className="text-xs text-muted-foreground">Taking longer than usual…</span>
              {onAbort ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto px-0 text-xs font-light italic text-muted-foreground hover:text-brand-hover"
                  onClick={onAbort}
                  disabled={isAborting}
                >
                  {abortLabel}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Stop feedback
// ============================================================================

function StoppingNotice() {
  return (
    <div
      className="flex w-full max-w-md flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100"
      role="status"
      aria-live="polite"
    >
      <Loader
        variant="circular"
        size="sm"
        className="border-amber-700/40 border-t-amber-700 dark:border-amber-200/40 dark:border-t-amber-200"
      />
      <span className="font-medium">Stopping agent…</span>
      <span className="text-amber-800/80 dark:text-amber-100/80">Finishing the current step.</span>
    </div>
  )
}

function StoppedNotice() {
  return (
    <div
      // className="flex w-full max-w-md flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-muted-foreground/25 bg-muted/60 px-3 py-2 text-sm"
      className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-tangarine-300 bg-tangarine-50 px-3 py-2 text-sm text-tangarine-300"
      role="status"
      aria-live="polite"
    >
      <Square className="size-3.5 shrink-0 fill-tangarine-500 text-tangarine-500" aria-hidden />
      {/* <Square className="size-3.5 shrink-0 fill-muted-foreground text-muted-foreground" aria-hidden /> */}
      <span className="font-medium text-tangarine-500">Response stopped</span>
      {/* <span className="font-medium text-foreground">Response stopped</span> */}
      <span className="text-tangarine-500">You cancelled this reply.</span>
      {/* <span className="text-muted-foreground">You cancelled this reply.</span> */}
    </div>
  )
}

function CompactingNotice() {
  return (
    <div
      className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-950 dark:text-sky-100"
      role="status"
      aria-live="polite"
    >
      <Loader
        variant="circular"
        size="sm"
        className="border-sky-700/40 border-t-sky-700 dark:border-sky-200/40 dark:border-t-sky-200"
      />
      <span className="font-medium">Compressing earlier context…</span>
      <span className="text-sky-900/80 dark:text-sky-100/80">
        Summarising older messages so the agent can keep responding.
      </span>
    </div>
  )
}

function CompactedNotice({ contextState }: { contextState: ChatContextState | null }) {
  const summarisedCount = contextState?.lastCompactionSummarised
  const detail =
    typeof summarisedCount === 'number' && summarisedCount > 0
      ? `${summarisedCount} earlier message${summarisedCount === 1 ? '' : 's'} were summarised. Your full chat history is unchanged.`
      : 'Earlier messages were summarised for the agent. Your full chat history is unchanged.'

  return (
    <div
      className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-700 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-200"
      role="status"
      aria-live="polite"
    >
      <Layers className="size-3.5 shrink-0 text-sky-600 dark:text-sky-300" aria-hidden />
      <span className="font-medium text-sky-700 dark:text-sky-200">Context compressed</span>
      <span className="text-sky-700 dark:text-sky-300">{detail}</span>
    </div>
  )
}

function CompactionSkippedNotice() {
  return (
    <div
      className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
      role="status"
      aria-live="polite"
    >
      <Layers className="size-3.5 shrink-0 text-amber-600 dark:text-amber-300" aria-hidden />
      <span className="font-medium text-amber-800 dark:text-amber-200">Context not compressed</span>
      <span className="text-amber-800/90 dark:text-amber-100/90">
        Thresholds were reached but there was no safe place to summarise yet. Try a longer conversation or lower
        keep-recent.
      </span>
    </div>
  )
}

// ============================================================================
// Reasoning Section (Collapsible)
// ============================================================================

interface ReasoningSectionProps {
  isStreaming: boolean
  hasContent: boolean
  thinking?: string
  reasoningSteps: AgentStep[]
}

function ReasoningSection({ isStreaming, hasContent, thinking, reasoningSteps }: ReasoningSectionProps) {
  const [isOpen, setIsOpen] = useState(isStreaming)
  const hasThinking = thinking && thinking.length > 0

  // Auto-open when streaming starts (thinking phase)
  useEffect(() => {
    if (isStreaming && !hasContent) {
      setIsOpen(true)
    }
  }, [isStreaming, hasContent])

  // Auto-collapse when response content starts streaming in
  useEffect(() => {
    if (hasContent) {
      setIsOpen(false)
    }
  }, [hasContent])

  return (
    <div className="rounded-lg border border-border bg-muted/30">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-muted-foreground hover:text-foreground"
      >
        <span>{isStreaming ? 'Thinking...' : 'View reasoning'}</span>
        <ArrowDown className={cn('size-4 transition-transform duration-200', isOpen ? 'rotate-180' : '')} />
      </button>
      {isOpen && (
        <div className="border-t border-border px-3 py-3">
          {/* Show thinking text if available */}
          {hasThinking && <div className="whitespace-pre-wrap text-sm text-muted-foreground">{thinking}</div>}
          {/* Show reasoning steps */}
          {reasoningSteps.map((step, index) => (
            <div key={`${step.stepNumber}-${index}`} className="mt-2 border-l-2 border-primary/30 pl-3">
              <div className="text-sm font-medium text-muted-foreground">Step {step.stepNumber}</div>
              {step.toolResult && <div className="mt-1 text-sm text-foreground">{step.toolResult}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Agent Tool Call
// ============================================================================

interface AgentToolCallProps {
  step: AgentStep
}

function AgentToolCall({ step }: AgentToolCallProps) {
  // Map agent step to Tool component format
  const getToolState = (): ToolPart['state'] => {
    if (step.toolResult) return 'output-available'
    if (step.toolArgs) return 'input-available'
    return 'input-streaming'
  }

  if (!step.toolName) return null

  const toolPart: ToolPart = {
    type: step.toolName,
    state: getToolState(),
    input: step.toolArgs,
    output: step.toolResult ? { result: step.toolResult } : undefined,
  }

  return (
    <div className="flex items-start gap-2">
      <Wrench className="mt-2.5 size-5 shrink-0 text-foreground/30" />
      <div className="flex-1">
        <Tool className="mt-0 border-none" toolPart={toolPart} />
      </div>
    </div>
  )
}
